import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { ApiResponseGrid } from "middleware/api";
import { RegionType, WorkzoneType } from "sagas/types/regions";
import { OverlapType, SlotTemplateType } from "sagas/types/slotTemplates";
import { RootState } from "store";
import {
  SlotTemplatesFilterType,
  SlotTemplatesStateType,
} from "./types/slotTemplates";

export const initialState: SlotTemplatesStateType = {
  fetchingWorkzones: false,
  filters: {},
  actualRegion: "",
  regions: [],
  workzones: [],
  templates: [],
  overlappedTemplates: [],
  seeMore: false,
  filtersExtra: {
    limit: 10,
    offset: 0,
  },
  filtersExtraSeeMore: {
    limit: 15,
    offset: 0,
  },
  showDetailModal: false,
  showCreationModal: false,
  collapseTable: true,
};

const SLICE_NAME = "slotTemplates";

export const slotTemplatesSlice = createSlice({
  name: SLICE_NAME,
  initialState,
  reducers: {
    getRegionsRequest: () => {},
    getRegionsError: () => {},
    getRegionsSuccess: (
      state: SlotTemplatesStateType,
      action: PayloadAction<RegionType[]>
    ) => {
      const { payload } = action;
      state.regions = payload;
    },
    getWorkzonesFetch: (
      state: SlotTemplatesStateType,
      action: PayloadAction<string>
    ) => {
      state.fetchingWorkzones = true;
    },
    getWorkzonesError: (state: SlotTemplatesStateType) => {
      state.fetchingWorkzones = false;
      state.workzones = [];
    },
    getWorkzonesSuccess: (
      state: SlotTemplatesStateType,
      action: PayloadAction<WorkzoneType[]>
    ) => {
      const { payload } = action;
      state.workzones = payload;
      state.fetchingWorkzones = false;
    },
    setActualRegion: (
      state: SlotTemplatesStateType,
      action: PayloadAction<string>
    ) => {
      state.actualRegion = action.payload;
    },
    setFilters: (
      state: SlotTemplatesStateType,
      action: PayloadAction<SlotTemplatesFilterType>
    ) => {
      state.filters = action.payload;
    },
    resetFilter: (state: SlotTemplatesStateType) => {
      state.filters = initialState.filters;
      state.filtersExtra = initialState.filtersExtra;
      state.filtersExtraSeeMore = initialState.filtersExtraSeeMore;
    },
    resetWorkzones: (state: SlotTemplatesStateType) => {
      state.workzones = [];
    },
    getSlotTemplatesRequest: (
      state: SlotTemplatesStateType,
      action: PayloadAction<SlotTemplatesFilterType>
    ) => {},
    getSlotTemplatesError: () => {},
    getSlotTemplatesSuccess: (
      state: SlotTemplatesStateType,
      action: PayloadAction<ApiResponseGrid<SlotTemplateType>>
    ) => {
      state.templates =
        action.payload.offset === 0
          ? action.payload.result.items
          : state.templates.concat(action.payload.result.items);
      state.seeMore = action.payload.hasMore;
      state.filtersExtraSeeMore.offset =
        action.payload.offset + action.payload.limit;
    },
    toggleDetailModalVisibility: (
      state: SlotTemplatesStateType,
      action: PayloadAction<boolean>
    ) => {
      state.showDetailModal = action.payload;
    },
    toggleCreationModalVisibility: (
      state: SlotTemplatesStateType,
      action: PayloadAction<boolean>
    ) => {
      state.showCreationModal = action.payload;
    },
    setOverlappedTemplates: (
      state: SlotTemplatesStateType,
      action: PayloadAction<OverlapType[]>
    ) => {
      state.overlappedTemplates = action.payload;
    },
    setCollapseTable: (state, action: PayloadAction<boolean>) => {
      state.collapseTable = action.payload;
    },
  },
});

export const slotTemplatesSelector = (state: RootState) => state.slotTemplates;

export const actions = slotTemplatesSlice.actions;

export default slotTemplatesSlice.reducer;
